import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * Encart "Souvent achetes ensemble".
 *
 * Deux modes :
 * - bundle : le produit de la page + des suggestions cochables, un seul bouton
 *   d'ajout et un total recalcule en direct (fiche produit).
 * - single : un bouton d'ajout par suggestion (tiroir panier).
 *
 * Suit le meme contrat que cross-sell.js : POST sur /cart/add.js avec la section
 * du tiroir, puis CartUpdatedEvent et cart:open.
 */
class BoughtTogether extends HTMLElement {
  constructor() {
    super();

    this.mode = 'bundle';
    this.form = null;
    this.currentPrices = {};
  }

  connectedCallback() {
    this.mode = this.dataset.mode || 'bundle';
    this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    this.currentPrices = this.#parseCurrentPrices();

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);

    this.#loadSuggestions();

    if (this.mode === 'bundle') {
      // La variante et la quantite du produit courant vivent dans le formulaire
      // de la fiche : on suit ses changements pour garder le total juste.
      this.form?.addEventListener('change', this.#handleChange);
      document.addEventListener('variant:updated', this.#handleChange);
      this.#updateSummary();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
    this.form?.removeEventListener('change', this.#handleChange);
    document.removeEventListener('variant:updated', this.#handleChange);
  }

  /**
   * Charge les produits complementaires via l'API de recommandations Shopify.
   * Leur metachamp n'etant pas lisible en Liquid, c'est le seul moyen de les
   * recuperer. Tant que la requete n'a rien renvoye, les lignes de repli
   * rendues cote serveur restent en place.
   */
  async #loadSuggestions() {
    const url = this.dataset.url;
    const list = this.querySelector('[data-bt-list]');
    if (!url || !list) return;

    try {
      const response = await fetch(url);
      const text = await response.text();

      const html = document.createElement('div');
      html.innerHTML = text;
      const rows = html.querySelectorAll('[data-bt-item]');
      if (rows.length === 0) return;

      list.querySelectorAll('[data-bt-item]').forEach((row) => row.remove());
      rows.forEach((row) => list.appendChild(row));

      if (this.dataset.buttonLabel) {
        list.querySelectorAll('[data-bt-add]').forEach((button) => {
          button.textContent = this.dataset.buttonLabel;
        });
      }

      this.hidden = false;
      if (this.mode === 'bundle') this.#updateSummary();
    } catch (error) {
      console.error('Error:', error);
    }
  }

  #parseCurrentPrices() {
    const source = this.querySelector('[data-bt-current-prices]');
    if (!source) return {};

    try {
      return JSON.parse(source.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  #handleChange = (event) => {
    const select = event.target?.closest?.('[data-bt-variant]');
    if (select) this.#updateRowPrice(select);

    if (this.mode === 'bundle') this.#updateSummary();
  };

  /**
   * Suggestions cochees, au format attendu par /cart/add.js.
   * Lu par les scripts de fiche produit qui gerent un cadeau offert : ils
   * ajoutent ces articles a leur propre requete pour n'avoir qu'un seul ajout.
   */
  getExtraItems() {
    const items = [];

    this.#getCheckedRows().forEach((row) => {
      const selection = this.#getRowSelection(row);
      if (selection.variantId) items.push({ id: selection.variantId, quantity: 1 });
    });

    return items;
  }

  #handleClick = (event) => {
    const button = event.target.closest('[data-bt-add]');
    if (!button) return;

    event.preventDefault();

    // Fiches avec cadeau offert : c'est le formulaire qui gere l'ajout, sinon
    // le cadeau serait perdu. Il recupere nos suggestions via getExtraItems().
    if (this.mode === 'bundle' && this.form?.dataset.btDelegate === 'true') {
      this.#delegateToForm(button);
      return;
    }

    const items = this.mode === 'single' ? this.#getSingleItems(button) : this.#getBundleItems();
    if (items.length === 0) return;

    this.#addToCart(items, button);
  };

  #delegateToForm(button) {
    button.classList.add('is-loading');
    button.disabled = true;

    const release = () => {
      button.classList.remove('is-loading');
      button.disabled = false;
      clearTimeout(timer);
    };
    const timer = setTimeout(release, 6000);
    document.addEventListener('cart:updated', release, { once: true });

    // Signale au formulaire que cet envoi vient de l'encart : lui seul ajoute
    // les suggestions cochees, le bouton principal reste inchange.
    // requestSubmit declenche l'evenement submit de facon synchrone : on retire
    // le drapeau juste apres, pour qu'une validation echouee ne le laisse pas
    // arme pour le clic suivant.
    this.form.dataset.btExtras = 'true';

    try {
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    } finally {
      delete this.form.dataset.btExtras;
    }
  }

  /** Ligne de suggestion : prix affiche selon la variante choisie. */
  #updateRowPrice(select) {
    const row = select.closest('[data-bt-item]');
    const priceTarget = row?.querySelector('[data-bt-item-price]');
    if (!priceTarget) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    priceTarget.textContent = formatMoney(parseInt(option.dataset.price, 10) || 0);
  }

  /** Variante + prix actuellement selectionnes sur une ligne de suggestion. */
  #getRowSelection(row) {
    const select = row.querySelector('[data-bt-variant]');

    if (select) {
      const option = select.options[select.selectedIndex];
      return {
        variantId: select.value,
        price: parseInt(option?.dataset.price, 10) || 0,
        compareAtPrice: parseInt(option?.dataset.compareAtPrice, 10) || parseInt(option?.dataset.price, 10) || 0,
      };
    }

    return {
      variantId: row.dataset.variantId,
      price: parseInt(row.dataset.price, 10) || 0,
      compareAtPrice: parseInt(row.dataset.compareAtPrice, 10) || parseInt(row.dataset.price, 10) || 0,
    };
  }

  #getCheckedRows() {
    return Array.from(this.querySelectorAll('[data-bt-item]')).filter((row) => {
      const checkbox = row.querySelector('[data-bt-checkbox]');
      return checkbox != null && checkbox.checked;
    });
  }

  /**
   * Produit de la page, lu dans le formulaire au moment du clic pour recuperer
   * la bonne variante, la quantite et les proprietes de ligne.
   * Meme approche que le handler d'ajout de product-details.liquid.
   */
  #getCurrentItem() {
    if (!this.form) {
      const variantId = this.dataset.currentVariantId;
      return variantId ? { id: variantId, quantity: 1 } : null;
    }

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    if (!variantId) return null;

    const item = {
      id: variantId,
      quantity: parseInt(formData.get('quantity'), 10) || 1,
    };

    const properties = {};
    formData.forEach((value, key) => {
      if (key.indexOf('properties[') === 0) {
        properties[key.replace('properties[', '').replace(']', '')] = value;
      }
    });
    if (Object.keys(properties).length > 0) item.properties = properties;

    return item;
  }

  /**
   * Articles du formulaire de la fiche. Les sections produit exposent
   * buildBaseItems() quand un cadeau accompagne le produit : on s'en sert pour
   * ne pas reimplementer leur logique.
   */
  #getBaseItems() {
    if (typeof this.form?.buildBaseItems === 'function') {
      try {
        const items = this.form.buildBaseItems();
        if (Array.isArray(items) && items.length > 0) return items;
      } catch (error) {
        console.error('Error:', error);
      }
    }

    const currentItem = this.#getCurrentItem();
    return currentItem ? [currentItem] : [];
  }

  #getBundleItems() {
    return this.#getBaseItems().concat(this.getExtraItems());
  }

  #getSingleItems(button) {
    const row = button.closest('[data-bt-item]');
    if (!row) return [];

    const selection = this.#getRowSelection(row);
    return selection.variantId ? [{ id: selection.variantId, quantity: 1 }] : [];
  }

  /** Prix de la variante actuellement selectionnee sur la fiche. */
  #getCurrentPrices() {
    const fallback = {
      price: parseInt(this.dataset.currentPrice, 10) || 0,
      compareAtPrice: parseInt(this.dataset.currentComparePrice, 10) || parseInt(this.dataset.currentPrice, 10) || 0,
    };

    const variantId = this.form ? new FormData(this.form).get('id') : this.dataset.currentVariantId;
    const prices = variantId ? this.currentPrices[variantId] : null;
    if (!prices) return fallback;

    return {
      price: prices.price || 0,
      compareAtPrice: prices.compare_at_price || prices.price || 0,
    };
  }

  #updateSummary() {
    const checkedRows = this.#getCheckedRows();
    const current = this.#getCurrentPrices();

    let total = current.price;
    let compareTotal = current.compareAtPrice;

    checkedRows.forEach((row) => {
      const selection = this.#getRowSelection(row);
      total += selection.price;
      compareTotal += selection.compareAtPrice;
    });

    const totalTarget = this.querySelector('[data-bt-total]');
    if (totalTarget) totalTarget.textContent = formatMoney(total);

    const compareTarget = this.querySelector('[data-bt-compare]');
    if (compareTarget) compareTarget.textContent = compareTotal > total ? formatMoney(compareTotal) : '';

    const button = this.querySelector('[data-bt-add]');
    if (button) {
      const count = checkedRows.length + 1;
      const template = count > 1 ? this.dataset.labelAddMulti : this.dataset.labelAdd;
      if (template) button.textContent = template.replace('{count}', count);
    }
  }

  async #addToCart(items, button) {
    button.classList.add('is-loading');
    button.disabled = true;

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items, sections: sections.join(',') }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || "Erreur lors de l'ajout au panier.");
      }

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount?.();
      const addedCount = items.reduce((total, item) => total + (item.quantity || 1), 0);

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: currentItemCount != null ? currentItemCount + addedCount : undefined,
            source: 'bought-together',
            sections: data.sections,
          },
        ),
      );

      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }
}

if (!customElements.get('bought-together')) {
  customElements.define('bought-together', BoughtTogether);
}
